/**
 * Admin WebAuthn / passkey hooks (WA-1a backend → WA-1b frontend).
 *
 * Six endpoints, all under /admin/mfa/webauthn/:
 *
 *   POST  /admin/mfa/webauthn/enroll/begin    — dual-mode (mfa_pending OR SA access JWT)
 *   POST  /admin/mfa/webauthn/enroll/finish   — dual-mode
 *   POST  /admin/mfa/webauthn/verify/begin    — mfa_pending JWT only
 *   POST  /admin/mfa/webauthn/verify/finish   — mfa_pending JWT only
 *   GET   /admin/mfa/webauthn/credentials     — SA access JWT
 *   DELETE /admin/mfa/webauthn/credentials/{id} — SA access JWT + password
 *
 * Auth header note:
 *   - Enroll begin/finish: ``usePendingOrAccessHeader`` — reads mfaPendingToken
 *     first (bootstrap path from login), falls back to accessToken (profile
 *     self-enable path via regular SA access JWT piped through apiClient).
 *   - Verify begin/finish: ``mfaPendingHeader`` — mfa_pending JWT only.
 *   - GET / DELETE: apiClient interceptor adds Authorization automatically
 *     (SA access JWT path).
 *
 * Response types mirror the WA-1a backend schemas verbatim.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { useAuthStore } from "@/stores/useAuthStore";

// ── Response types ────────────────────────────────────────────────────────────

/** ``WebauthnBeginResponse`` — the py_webauthn options blob the browser needs. */
export interface WebauthnBeginResponse {
  options: Record<string, unknown>;
}

/** ``AdminMfaVerifyResponse`` — standard access-token envelope. */
export interface WebauthnFinishResponse {
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
}

/** Per-passkey metadata row (no public key, no raw credential bytes). */
export interface WebauthnCredentialItem {
  id: string;
  nickname: string | null;
  created_at: string;
  last_used_at: string | null;
}

interface WebauthnCredentialsListResponse {
  items: WebauthnCredentialItem[];
}

// ── Auth helpers ──────────────────────────────────────────────────────────────

/**
 * For enroll endpoints: prefer the mfa_pending token (bootstrap from login),
 * fall back to the regular access token (in-profile self-enable). The backend's
 * ``require_mfa_pending_or_sa_access`` dep accepts both shapes.
 */
function pendingOrAccessHeader(): { Authorization: string } {
  const { mfaPendingToken, accessToken } = useAuthStore.getState();
  const token = mfaPendingToken ?? accessToken;
  if (!token) {
    throw new Error("No auth token available for WebAuthn enroll");
  }
  return { Authorization: `Bearer ${token}` };
}

/** For verify endpoints: mfa_pending JWT only. */
function mfaPendingHeader(): { Authorization: string } {
  const token = useAuthStore.getState().mfaPendingToken;
  if (!token) {
    throw new Error("mfaPendingToken missing from store");
  }
  return { Authorization: `Bearer ${token}` };
}

// ── Query keys ────────────────────────────────────────────────────────────────

const WEBAUTHN_CREDENTIALS_KEY = ["admin", "webauthn", "credentials"] as const;

// ── Hooks ─────────────────────────────────────────────────────────────────────

/** POST /admin/mfa/webauthn/enroll/begin — get registration options (dual-mode auth). */
export function useWebauthnEnrollBegin() {
  return useMutation<WebauthnBeginResponse, ApiError, void>({
    mutationFn: async () => {
      const resp = await apiClient.post<WebauthnBeginResponse>(
        "/admin/mfa/webauthn/enroll/begin",
        undefined,
        { headers: pendingOrAccessHeader() },
      );
      return resp.data;
    },
  });
}

/** POST /admin/mfa/webauthn/enroll/finish — submit attestation + optional nickname. */
export function useWebauthnEnrollFinish() {
  return useMutation<
    WebauthnFinishResponse,
    ApiError,
    { credential: Record<string, unknown>; nickname?: string | null }
  >({
    mutationFn: async ({ credential, nickname }) => {
      const resp = await apiClient.post<WebauthnFinishResponse>(
        "/admin/mfa/webauthn/enroll/finish",
        { credential, nickname: nickname ?? null },
        { headers: pendingOrAccessHeader() },
      );
      return resp.data;
    },
  });
}

/** POST /admin/mfa/webauthn/verify/begin — get assertion options (mfa_pending JWT). */
export function useWebauthnVerifyBegin() {
  return useMutation<WebauthnBeginResponse, ApiError, void>({
    mutationFn: async () => {
      const resp = await apiClient.post<WebauthnBeginResponse>(
        "/admin/mfa/webauthn/verify/begin",
        undefined,
        { headers: mfaPendingHeader() },
      );
      return resp.data;
    },
  });
}

/** POST /admin/mfa/webauthn/verify/finish — submit assertion (mfa_pending JWT). */
export function useWebauthnVerifyFinish() {
  return useMutation<WebauthnFinishResponse, ApiError, { credential: Record<string, unknown> }>({
    mutationFn: async ({ credential }) => {
      const resp = await apiClient.post<WebauthnFinishResponse>(
        "/admin/mfa/webauthn/verify/finish",
        { credential },
        { headers: mfaPendingHeader() },
      );
      return resp.data;
    },
  });
}

/** GET /admin/mfa/webauthn/credentials — list SA's passkeys (SA access JWT via interceptor). */
export function useWebauthnCredentials() {
  return useQuery<WebauthnCredentialsListResponse, ApiError>({
    queryKey: WEBAUTHN_CREDENTIALS_KEY,
    queryFn: async () => {
      const resp = await apiClient.get<WebauthnCredentialsListResponse>(
        "/admin/mfa/webauthn/credentials",
      );
      return resp.data;
    },
    staleTime: 30_000,
  });
}

/** DELETE /admin/mfa/webauthn/credentials/{id} — password-only (no TOTP). */
export function useDeleteWebauthnCredential() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { credential_id: string; current_password: string }>({
    mutationFn: async ({ credential_id, current_password }) => {
      await apiClient.delete(`/admin/mfa/webauthn/credentials/${credential_id}`, {
        data: { current_password },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WEBAUTHN_CREDENTIALS_KEY });
    },
  });
}
